import { useReducer, useCallback } from "react";

// Action types for better code organization
export const ACTIONS = {
  FIELD_CHANGE: "FIELD_CHANGE",
  SET_LOADING: "SET_LOADING",
  SET_MESSAGE: "SET_MESSAGE",
  LOAD_DATA: "LOAD_DATA",
  RESET_FORM: "RESET_FORM",
  SET_FORM_DATA: "SET_FORM_DATA",
  SET_SECTION_DATA: "SET_SECTION_DATA",
  SET_TABLE_DATA: "SET_TABLE_DATA",
  SET_TABLE_LOADING: "SET_TABLE_LOADING",
  SET_TABLE_PAGINATION: "SET_TABLE_PAGINATION",
  SET_TABLE_SEARCH: "SET_TABLE_SEARCH",
  SET_TABLE_SORT: "SET_TABLE_SORT",
  SET_ERROR: "SET_ERROR",
  SET_IMAGE: "SET_IMAGE",
  SET_TAB: "SET_TAB",
  SET_UI_STATE: "SET_UI_STATE",
};

// The core reducer that handles common admin page actions
export function adminReducer(state, action) {
  switch (action.type) {
    case ACTIONS.FIELD_CHANGE:
      return {
        ...state,
        [action.formName]: {
          ...state[action.formName],
          [action.field]: action.value,
          [`${action.field}_error`]: false,
        },
      };

    case ACTIONS.SET_ERROR:
      return {
        ...state,
        [action.formName]: {
          ...state[action.formName],
          [`${action.field}_error`]: true,
        },
      };

    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.loading };

    case ACTIONS.SET_MESSAGE:
      return {
        ...state,
        successMsg: action.success || "",
        errorMsg: action.error || "",
      };

    case ACTIONS.RESET_FORM:
      // Support both specific form reset and full reset
      return action.formName
        ? {
            ...state,
            [action.formName]:
              action.defaultValues || state.initialState[action.formName],
            ...(action.clearMessages ? { successMsg: "", errorMsg: "" } : {}),
          }
        : {
            ...state,
            formData: state.initialState.formData,
            images: state.initialState.images,
            successMsg: "",
            errorMsg: "",
          };

    case ACTIONS.SET_FORM_DATA:
      return {
        ...state,
        formData: {
          ...state.formData,
          ...action.data,
        },
      };

    case ACTIONS.SET_SECTION_DATA:
      return {
        ...state,
        sectionData: {
          ...state.sectionData,
          ...action.data,
        },
      };

    case ACTIONS.LOAD_DATA:
      return { ...state, ...action.data };

    case ACTIONS.SET_TABLE_DATA:
      return {
        ...state,
        tableData: {
          ...state.tableData,
          data: action.data,
          ...(action.pagination ? { pagination: action.pagination } : {}),
          loading: false,
        },
      };

    case ACTIONS.SET_TABLE_LOADING:
      return {
        ...state,
        tableData: {
          ...state.tableData,
          loading: action.loading,
        },
      };

    case ACTIONS.SET_TABLE_PAGINATION:
      return {
        ...state,
        tableData: {
          ...state.tableData,
          pagination: {
            ...state.tableData.pagination,
            ...action.pagination,
          },
        },
      };

    case ACTIONS.SET_TABLE_SEARCH:
      return {
        ...state,
        tableData: {
          ...state.tableData,
          search: {
            ...state.tableData.search,
            ...action.search,
          },
        },
      };

    case ACTIONS.SET_TABLE_SORT:
      return {
        ...state,
        tableData: {
          ...state.tableData,
          sort: {
            field: action.field,
            direction: action.direction,
          },
        },
      };

    case ACTIONS.SET_IMAGE:
      return {
        ...state,
        [action.formName]: {
          ...state[action.formName],
          [action.imageField]: action.file,
          [`${action.imageField}_name`]: action.fileName,
          [`${action.imageField}_error`]: false,
        },
        images: {
          ...state.images,
          [action.previewField]: action.previewUrl,
        },
      };

    case ACTIONS.SET_TAB:
      return {
        ...state,
        activeTab: action.tab,
      };

    case ACTIONS.SET_UI_STATE:
      return {
        ...state,
        ui: {
          ...state.ui,
          ...action.ui,
        },
      };

    // For page-specific actions
    default:
      return state.pageReducer ? state.pageReducer(state, action) : state;
  }
}

// Custom hook for admin pages
export function useAdminReducer(initialState, pageReducer) {
  // Store initial state for reset operations
  const stateWithInitial = {
    ...initialState,
    initialState,
    loading: false,
    successMsg: "",
    errorMsg: "",
  };

  const [state, dispatch] = useReducer((state, action) => {
    // page-specific reducer to the state for use by adminReducer
    const stateWithReducer = { ...state, pageReducer };
    return adminReducer(stateWithReducer, action);
  }, stateWithInitial);

  // Common helper functions
  const setLoading = useCallback((loading) => {
    dispatch({ type: ACTIONS.SET_LOADING, loading });
  }, []);

  // Utility for checking if loading is active
  const isLoading = state.loading;

  const setMessage = useCallback((success = "", error = "") => {
    dispatch({ type: ACTIONS.SET_MESSAGE, success, error });
  }, []);

  const handleFieldChange = useCallback(
    (e, formName = "formData") => {
      const { name, value } = e.target;
      const form = state[formName];
      const maxLength = form[`${name}_maxlenth`];

      // Only update if within character limits
      if (!maxLength || value.length <= maxLength) {
        dispatch({
          type: ACTIONS.FIELD_CHANGE,
          formName,
          field: name,
          value,
        });
      }
    },
    [state],
  );

  const handleFormError = useCallback(
    (field, error = true, formName = "formData") => {
      if (error) {
        dispatch({
          type: ACTIONS.SET_ERROR,
          formName,
          field,
        });
      }
    },
    [],
  );

  // Reset form to initial state
  const resetForm = useCallback(() => {
    dispatch({ type: ACTIONS.RESET_FORM });
  }, []);

  // Set form data (usually for edit operations)
  const setFormData = useCallback((data) => {
    dispatch({ type: ACTIONS.SET_FORM_DATA, data });
  }, []);

  // Set section data
  const setSectionData = useCallback((data) => {
    dispatch({ type: ACTIONS.SET_SECTION_DATA, data });
  }, []);

  // Handle image uploads with file previews
  const handleImageChange = useCallback(
    (event, imageField, previewField, fileName = null, previewUrl = null) => {
      // If files are provided, use those
      if (event.target.files && event.target.files[0]) {
        const file = event.target.files[0];
        dispatch({
          type: ACTIONS.SET_IMAGE,
          formName: "formData",
          imageField,
          file,
          fileName: file.name,
          previewField,
          previewUrl: URL.createObjectURL(file),
        });
      }
      // Otherwise use provided fileName and previewUrl (for loading existing images)
      else if (fileName && previewUrl) {
        dispatch({
          type: ACTIONS.SET_IMAGE,
          formName: "formData",
          imageField,
          file: null,
          fileName,
          previewField,
          previewUrl,
        });
      }
    },
    [],
  );

  // Table-related functions
  const setTableData = useCallback((data) => {
    dispatch({ type: ACTIONS.SET_TABLE_DATA, ...data });
  }, []);

  const setTableLoading = useCallback((loading) => {
    dispatch({ type: ACTIONS.SET_TABLE_LOADING, loading });
  }, []);

  const setTableSearch = useCallback((search) => {
    dispatch({ type: ACTIONS.SET_TABLE_SEARCH, search });
  }, []);

  const setTableSort = useCallback((field, direction) => {
    dispatch({ type: ACTIONS.SET_TABLE_SORT, field, direction });
  }, []);

  const setTablePagination = useCallback((pagination) => {
    dispatch({ type: ACTIONS.SET_TABLE_PAGINATION, pagination });
  }, []);

  // Set active tab
  const setActiveTab = useCallback((tab) => {
    dispatch({ type: ACTIONS.SET_TAB, tab });
  }, []);

  return {
    state,
    dispatch,
    setLoading,
    isLoading,
    setMessage,
    handleFieldChange,
    handleFormError,
    resetForm,
    setFormData,
    setSectionData,
    handleImageChange,
    setTableData,
    setTableLoading,
    setTableSearch,
    setTableSort,
    setTablePagination,
    setActiveTab,
  };
}
